import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Trash2, Pencil, Plus } from 'lucide-react';
import { useApiCaller } from '../../utils/ApiUtilities';
import {
    getAdminVocabWordsUrl,
    getAdminVocabWordUrl,
    getAdminVocabWordActionUrl,
    getAdminVocabWordsBulkApproveUrl,
    getAdminVocabQuestionsUrl,
    getAdminVocabQuestionUrl,
    getAdminVocabQuestionActionUrl,
    getAdminVocabQuestionsBulkApproveUrl,
} from '../../utils/UrlUtilities';
import { VOCAB_EXAM_TABS, VOCAB_GRE_SOURCES } from '../../utils/Constants';

// Admin surface for the Vocabulary Store: review pushed content (approve/reject/edit/delete) and
// manually author words + practice MCQs. Two tabs (Words / Questions); default filter = pending.

const STATUS_BADGE = {
    PENDING_REVIEW: { bg: '#fef9c3', color: '#92400e' },
    APPROVED: { bg: '#d1fae5', color: '#065f46' },
    REJECTED: { bg: '#fee2e2', color: '#991b1b' },
};
const EXAMS = VOCAB_EXAM_TABS.filter(t => t.value !== 'ielts_toefl').concat([{ value: 'toefl', label: 'TOEFL' }]);

const csvToList = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);

function WordForm({ initial, onSubmit, onCancel }) {
    const [f, setF] = useState(() => ({
        word: '', part_of_speech: '', meaning: '', bangla_meaning: '', example: '',
        synonyms: '', antonyms: '', pronunciation: '', exam: 'gre', source: 'barrons',
        ...(initial ? {
            ...initial,
            synonyms: (initial.synonyms || []).join(', '),
            antonyms: (initial.antonyms || []).join(', '),
        } : {}),
    }));
    const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
    const submit = () => {
        if (!f.word.trim()) return;
        onSubmit({
            word: f.word.trim(), part_of_speech: f.part_of_speech, meaning: f.meaning,
            bangla_meaning: f.bangla_meaning, example: f.example, pronunciation: f.pronunciation,
            synonyms: csvToList(f.synonyms), antonyms: csvToList(f.antonyms),
            exam: f.exam, source: f.exam === 'gre' ? f.source : '',
        });
    };
    return (
        <div className="card mb-3"><div className="card-body">
            <div className="row g-2">
                <div className="col-md-4"><label className="form-label small">Word *</label><input className="form-control form-control-sm" value={f.word} onChange={set('word')} /></div>
                <div className="col-md-4"><label className="form-label small">Part of speech</label><input className="form-control form-control-sm" value={f.part_of_speech} onChange={set('part_of_speech')} /></div>
                <div className="col-md-4"><label className="form-label small">Pronunciation</label><input className="form-control form-control-sm" value={f.pronunciation} onChange={set('pronunciation')} /></div>
                <div className="col-md-6"><label className="form-label small">Meaning</label><textarea className="form-control form-control-sm" rows={2} value={f.meaning} onChange={set('meaning')} /></div>
                <div className="col-md-6"><label className="form-label small">Bangla meaning</label><textarea className="form-control form-control-sm" rows={2} value={f.bangla_meaning} onChange={set('bangla_meaning')} /></div>
                <div className="col-md-12"><label className="form-label small">Example</label><input className="form-control form-control-sm" value={f.example} onChange={set('example')} /></div>
                <div className="col-md-6"><label className="form-label small">Synonyms (comma-separated)</label><input className="form-control form-control-sm" value={f.synonyms} onChange={set('synonyms')} /></div>
                <div className="col-md-6"><label className="form-label small">Antonyms (comma-separated)</label><input className="form-control form-control-sm" value={f.antonyms} onChange={set('antonyms')} /></div>
                <div className="col-md-3"><label className="form-label small">Exam</label><select className="form-select form-select-sm" value={f.exam} onChange={set('exam')}>{EXAMS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}</select></div>
                {f.exam === 'gre' && <div className="col-md-3"><label className="form-label small">Source</label><select className="form-select form-select-sm" value={f.source} onChange={set('source')}>{VOCAB_GRE_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>}
            </div>
            <div className="mt-2 d-flex gap-2">
                <button className="btn btn-sm btn-primary" onClick={submit}>Save</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>
            </div>
        </div></div>
    );
}

function QuestionForm({ initial, onSubmit, onCancel }) {
    const [f, setF] = useState(() => ({
        question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
        correct_option: 'A', answer_explanation: '', exam: 'gre', source: 'barrons', ...(initial || {}),
    }));
    const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
    const submit = () => {
        if (!f.question_text.trim()) return;
        onSubmit({
            question_text: f.question_text, option_a: f.option_a, option_b: f.option_b,
            option_c: f.option_c, option_d: f.option_d, correct_option: (f.correct_option || '').toUpperCase(),
            answer_explanation: f.answer_explanation, exam: f.exam, source: f.exam === 'gre' ? f.source : '',
        });
    };
    return (
        <div className="card mb-3"><div className="card-body">
            <div className="row g-2">
                <div className="col-12"><label className="form-label small">Question *</label><textarea className="form-control form-control-sm" rows={2} value={f.question_text} onChange={set('question_text')} /></div>
                {['a', 'b', 'c', 'd'].map(l => (
                    <div className="col-md-6" key={l}><label className="form-label small">Option {l.toUpperCase()}</label><input className="form-control form-control-sm" value={f[`option_${l}`]} onChange={set(`option_${l}`)} /></div>
                ))}
                <div className="col-md-3"><label className="form-label small">Correct (A-D)</label><input className="form-control form-control-sm" value={f.correct_option} onChange={set('correct_option')} /></div>
                <div className="col-md-3"><label className="form-label small">Exam</label><select className="form-select form-select-sm" value={f.exam} onChange={set('exam')}>{EXAMS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}</select></div>
                {f.exam === 'gre' && <div className="col-md-3"><label className="form-label small">Source</label><select className="form-select form-select-sm" value={f.source} onChange={set('source')}>{VOCAB_GRE_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>}
                <div className="col-12"><label className="form-label small">Explanation</label><textarea className="form-control form-control-sm" rows={2} value={f.answer_explanation} onChange={set('answer_explanation')} /></div>
            </div>
            <div className="mt-2 d-flex gap-2">
                <button className="btn btn-sm btn-primary" onClick={submit}>Save</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>
            </div>
        </div></div>
    );
}

// Centered modal overlay for the add/edit form.
function Modal({ title, onClose, children }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1080, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '2rem 1rem' }}>
            <div style={{ background: '#fff', borderRadius: '0.75rem', width: '100%', maxWidth: 720, boxShadow: '0 20px 50px -12px rgba(0,0,0,0.4)' }}>
                <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <h5 className="mb-0 fw-bold" style={{ color: '#4c1d95' }}>{title}</h5>
                    <button className="btn btn-sm btn-outline-secondary" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-3">{children}</div>
            </div>
        </div>
    );
}

// The shared review/management surface (Words / Questions tabs, filters, search, bulk approve,
// approve/reject/edit-in-modal). Embedded both in the admin Ingest page (review queue) and in the
// standalone Vocabulary Management page.
export function VocabularyReview({ defaultStatus = 'PENDING_REVIEW' } = {}) {
    const CallApi = useApiCaller();
    const [tab, setTab] = useState('words'); // 'words' | 'questions'
    const [status, setStatus] = useState(defaultStatus);
    const [exam, setExam] = useState('');
    const [source, setSource] = useState('');
    const [search, setSearch] = useState('');
    const [searchQ, setSearchQ] = useState(''); // debounced value actually sent
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(() => new Set());
    const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', row }

    const isWords = tab === 'words';

    // Debounce the search box so we don't refetch on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setSearchQ(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        setSelected(new Set());
        try {
            const params = {
                status: status || undefined, exam: exam || undefined,
                source: source || undefined, search: searchQ || undefined, limit: 1000,
            };
            const url = isWords ? getAdminVocabWordsUrl(params) : getAdminVocabQuestionsUrl(params);
            const res = await CallApi({ url, method: 'GET', token: true });
            setRows(res.success ? (isWords ? res.data.words : res.data.questions) || [] : []);
        } finally {
            setLoading(false);
        }
    }, [CallApi, isWords, status, exam, source, searchQ]);

    useEffect(() => { load(); }, [load]);

    const doAction = async (id, action) => {
        const url = isWords ? getAdminVocabWordActionUrl(id, action) : getAdminVocabQuestionActionUrl(id, action);
        const res = await CallApi({ url, method: 'POST', token: true });
        // Update the row in place so it stays visible (like the usual ingest flow) instead of
        // vanishing from a status-filtered list on refetch.
        if (res.success) setRows(prev => prev.map(r => (r.id === id ? { ...r, status: res.data.status } : r)));
    };
    const doDelete = async (id) => {
        if (!window.confirm('Delete this entry permanently?')) return;
        const url = isWords ? getAdminVocabWordUrl(id) : getAdminVocabQuestionUrl(id);
        const res = await CallApi({ url, method: 'DELETE', token: true });
        if (res.success) setRows(prev => prev.filter(r => r.id !== id));
    };
    const doCreate = async (payload) => {
        const url = isWords ? getAdminVocabWordsUrl() : getAdminVocabQuestionsUrl();
        const res = await CallApi({ url, method: 'POST', payload, token: true });
        if (res.success) { setModal(null); load(); }
    };
    const doUpdate = async (id, payload) => {
        const url = isWords ? getAdminVocabWordUrl(id) : getAdminVocabQuestionUrl(id);
        const res = await CallApi({ url, method: 'PUT', payload, token: true });
        if (res.success) {
            // Word update returns the row; question update returns {questions:[row]}.
            const updated = isWords ? res.data : (res.data.questions && res.data.questions[0]);
            setRows(prev => prev.map(r => (r.id === id ? (updated || r) : r)));
            setModal(null);
        }
    };
    const bulkApprove = async () => {
        if (!selected.size) return;
        const url = isWords ? getAdminVocabWordsBulkApproveUrl() : getAdminVocabQuestionsBulkApproveUrl();
        const res = await CallApi({ url, method: 'POST', payload: { ids: [...selected] }, token: true });
        if (res.success) {
            setRows(prev => prev.map(r => (selected.has(r.id) ? { ...r, status: 'APPROVED' } : r)));
            setSelected(new Set());
        }
    };

    const toggleRow = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map(r => r.id)));

    return (
        <div>
            <ul className="nav nav-tabs mb-3">
                <li className="nav-item"><button className={`nav-link ${isWords ? 'active' : ''}`} onClick={() => { setTab('words'); }}>Words</button></li>
                <li className="nav-item"><button className={`nav-link ${!isWords ? 'active' : ''}`} onClick={() => { setTab('questions'); }}>Questions</button></li>
            </ul>

            <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div><label className="form-label small mb-0">Search {isWords ? 'word' : 'question'}</label>
                    <input className="form-control form-control-sm" style={{ minWidth: 200 }} placeholder={isWords ? 'Search word…' : 'Search question…'} value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div><label className="form-label small mb-0">Status</label>
                    <select className="form-select form-select-sm" value={status} onChange={e => setStatus(e.target.value)}>
                        <option value="">All</option>
                        <option value="PENDING_REVIEW">Pending Review</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                    </select>
                </div>
                <div><label className="form-label small mb-0">Exam</label>
                    <select className="form-select form-select-sm" value={exam} onChange={e => setExam(e.target.value)}>
                        <option value="">All</option>
                        {EXAMS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                    </select>
                </div>
                <div><label className="form-label small mb-0">Source</label>
                    <select className="form-select form-select-sm" value={source} onChange={e => setSource(e.target.value)}>
                        <option value="">All</option>
                        {VOCAB_GRE_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={load}>Refresh</button>
                <button className="btn btn-sm btn-success ms-auto" onClick={() => setModal({ mode: 'add' })}>
                    <Plus size={15} style={{ marginRight: 4 }} />Add {isWords ? 'Word' : 'Question'}
                </button>
            </div>

            {/* Bulk actions bar */}
            <div className="d-flex align-items-center gap-2 mb-2">
                <button className="btn btn-sm btn-success" disabled={!selected.size} onClick={bulkApprove}>
                    <Check size={15} style={{ marginRight: 4 }} />Approve Selected ({selected.size})
                </button>
                {selected.size > 0 && <button className="btn btn-sm btn-link" onClick={() => setSelected(new Set())}>Clear selection</button>}
            </div>

            {loading ? <div className="text-muted p-3">Loading…</div> : (
                <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                        <thead><tr>
                            <th style={{ width: 34 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                            <th>#</th>
                            <th>{isWords ? 'Word' : 'Question'}</th>
                            <th>{isWords ? 'Meaning' : 'Correct'}</th>
                            <th>Exam</th><th>Source</th><th>Status</th><th></th>
                        </tr></thead>
                        <tbody>
                            {rows.length === 0 && <tr><td colSpan={8} className="text-muted text-center py-3">No entries.</td></tr>}
                            {rows.map(r => {
                                const sb = STATUS_BADGE[r.status] || {};
                                return (
                                    <tr key={r.id}>
                                        <td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} /></td>
                                        <td>{r.id}</td>
                                        <td style={{ maxWidth: 280 }} className="text-truncate" title={isWords ? r.word : r.question_text}>{isWords ? r.word : r.question_text}</td>
                                        <td style={{ maxWidth: 260 }} className="text-truncate">{isWords ? r.meaning : r.correct_option}</td>
                                        <td>{r.exam}</td><td>{r.source || '—'}</td>
                                        <td><span className="badge rounded-pill" style={{ background: sb.bg, color: sb.color }}>{r.status}</span></td>
                                        <td className="text-nowrap">
                                            {r.status !== 'APPROVED' && <button className="btn btn-sm btn-outline-success me-1" title="Approve" onClick={() => doAction(r.id, 'approve')}><Check size={14} /></button>}
                                            {r.status !== 'REJECTED' && <button className="btn btn-sm btn-outline-warning me-1" title="Reject" onClick={() => doAction(r.id, 'reject')}><X size={14} /></button>}
                                            <button className="btn btn-sm btn-outline-primary me-1" title="Edit" onClick={() => setModal({ mode: 'edit', row: r })}><Pencil size={14} /></button>
                                            <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => doDelete(r.id)}><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {modal && (
                <Modal
                    title={`${modal.mode === 'add' ? 'Add' : 'Edit'} ${isWords ? 'Word' : 'Question'}`}
                    onClose={() => setModal(null)}
                >
                    {isWords ? (
                        <WordForm
                            initial={modal.mode === 'edit' ? modal.row : null}
                            onSubmit={(p) => (modal.mode === 'edit' ? doUpdate(modal.row.id, p) : doCreate(p))}
                            onCancel={() => setModal(null)}
                        />
                    ) : (
                        <QuestionForm
                            initial={modal.mode === 'edit' ? modal.row : null}
                            onSubmit={(p) => (modal.mode === 'edit' ? doUpdate(modal.row.id, p) : doCreate(p))}
                            onCancel={() => setModal(null)}
                        />
                    )}
                </Modal>
            )}
        </div>
    );
}

// Standalone page (/admin/vocabulary).
export default function VocabularyManagement() {
    return (
        <div className="container py-4">
            <h1 className="h3 fw-bold mb-3" style={{ color: '#4c1d95' }}>Vocabulary Management</h1>
            <VocabularyReview defaultStatus="APPROVED" />
        </div>
    );
}
